import React, { useCallback, useContext } from 'react';
import Button from '@amzn/meridian/button';
import { isFeatureEnabled, Feature } from '../../config/FeaturesConfig';
import Link from '@amzn/meridian/link';
import { renderUrl } from '../../config/Url';
import { getCSCEndpoint } from '../../config/Config';
import DataSource from '../../api';
import { StateContext } from '../../GlobalStateStore';
import { useParams } from 'react-router';
import { getCscEntityPrefix } from '../../config/Config';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import { CLClickstream } from './utils/ContactListeningHelpers';
import useCustomTracking from '../../Util/useCustomTracking';

export interface ContactListeningProps {
  contactID: string;
  setContactListening: any;
  setContactListeningState: any;
  setShowRecordingModal: any;
  setContactListeningParams: any;
  setRecordingContactId: any;
  clickstreamTags: any;
  cscEndpoint?: string;
  cscTarget?: string;
  cscRel?: string;
  contactStartDate?: string;
  contactEndDate?: string;
  contactHandlerId?: string;
  isRepeatContact?: boolean;
  repeatEncryptedContactID?: string;
  CLClickstream: CLClickstream;
  displayAsLink?: boolean;
}

const ContactListening: React.FC<ContactListeningProps> = ({
  contactID,
  setContactListening,
  setContactListeningState,
  setShowRecordingModal,
  setContactListeningParams,
  setRecordingContactId,
  clickstreamTags,
  cscEndpoint,
  cscTarget,
  cscRel,
  contactStartDate,
  contactEndDate,
  contactHandlerId,
  isRepeatContact,
  CLClickstream,
  repeatEncryptedContactID,
  displayAsLink,
}: ContactListeningProps): React.ReactElement => {
  const contactLinksEnabled = isFeatureEnabled(Feature.CONTACT_LINKS);
  const contactListeningEnabled = isFeatureEnabled(Feature.CONTACT_LISTENING);
  const contactListeningWithChatEnabled = isFeatureEnabled(
    Feature.CONTACT_LISTENING_WITH_CHAT
  );
  const contactListeningWithNewAuthEnabled = isFeatureEnabled(
    Feature.CONTACT_LISTENING_WITH_NEW_AUTH_FLOW
  );

  const isNewAuthPath = contactListeningWithNewAuthEnabled && isRepeatContact;

  const defaultContactStartDate = moment()
    .subtract(1, 'years')
    .format('YYYY-MM-DD');
  const defaultContactEndDate = moment().format('YYYY-MM-DD');

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { userID, requesterUsername, requesterOwner, isTm } =
    inspireOptions.data;
  const routeParams = useParams();
  const routeUserID = routeParams?.userId;
  const subject = `${requesterUsername}@${requesterOwner.toLowerCase()}`;

  if (!contactHandlerId) {
    contactHandlerId =
      getCscEntityPrefix() + ':' + (routeUserID ? routeUserID : userID);
  }

  const [OpenCLModalTrackingTag, openCLModalClick] = useCustomTracking(
    `contact-listening-${contactID}`,
    CLClickstream.slotID,
    {
      'data-csa-c-type': 'button',
      'data-csa-c-tag1': CLClickstream.tag1,
      'data-csa-c-tag2': CLClickstream.tag2,
      'data-csa-c-tag3': CLClickstream.tag3,
      'data-csa-c-tag4': CLClickstream.tag4,
      'data-csa-c-tag5': CLClickstream.tag5,
      'data-csa-c-contactid': contactID,
      'data-csa-c-is-tm': isTm,
      'data-csa-c-username': requesterUsername,
    }
  );

  const onClickContactId = useCallback((contactId: string) => {
    openCLModalClick();
    setRecordingContactId(contactId);
    const apiNaws = DataSource.getDataFeed('NAWS');
    const queryVariables = apiNaws.prepareContactListeningDataQuery(
      contactHandlerId,
      contactId,
      contactStartDate ? contactStartDate : defaultContactStartDate,
      contactEndDate ? contactEndDate : defaultContactEndDate,
      subject,
      isNewAuthPath
    );
    setContactListeningParams(queryVariables);
    if (contactListeningWithChatEnabled) {
      apiNaws.getContactListeningDataWithChat(
        queryVariables,
        setContactListening,
        setContactListeningState
      );
    } else {
      apiNaws.getContactListeningData(
        queryVariables,
        setContactListening,
        setContactListeningState
      );
    }
    setShowRecordingModal(true);
  }, []);
  return contactListeningEnabled && !isRepeatContact ? (
    <>
      {displayAsLink ? (
        <Link onClick={() => onClickContactId(contactID)}>{contactID}</Link>
      ) : (
        <Button onClick={() => onClickContactId(contactID)} type='link'>
          {contactID}
        </Button>
      )}
      <OpenCLModalTrackingTag />
    </>
  ) : isNewAuthPath ? (
    <>
      {displayAsLink ? (
        <Link onClick={() => onClickContactId(repeatEncryptedContactID!)}>
          {contactID}
        </Link>
      ) : (
        <Button
          onClick={() => onClickContactId(repeatEncryptedContactID!)}
          type='link'
        >
          {contactID}
        </Button>
      )}
      <OpenCLModalTrackingTag />
    </>
  ) : contactLinksEnabled ? (
    <Link
      data-csa-c-type='link'
      {...clickstreamTags}
      href={renderUrl(cscEndpoint!, {
        externalDomain: getCSCEndpoint(),
      })}
      target={cscTarget}
      type='secondary'
      key={contactID}
      rel={cscRel}
    >
      {contactID}
    </Link>
  ) : (
    <Text>{contactID}</Text>
  );
};
export default ContactListening;
